
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface CoreValueChartProps {
  data: Array<{ date: string; count: number }>;
  title: string;
  color: string;
}

const CoreValueChart = ({ data, title, color }: CoreValueChartProps) => {
  // Process and deduplicate data to ensure proper date formatting and unique dates
  const processedData = (() => {
    if (!data || data.length === 0) return [];
    
    // Group by date and sum counts to handle duplicates
    const dateMap = new Map<string, number>();
    
    data.forEach(item => {
      if (!item.date || typeof item.count !== 'number') return;
      
      const dateKey = new Date(item.date).toISOString().split('T')[0]; // YYYY-MM-DD format
      const currentCount = dateMap.get(dateKey) || 0;
      dateMap.set(dateKey, currentCount + item.count);
    });
    
    // Convert back to array and sort by date (newest first, then reverse for display)
    const sortedEntries = Array.from(dateMap.entries())
      .sort((a, b) => new Date(b[0]).getTime() - new Date(a[0]).getTime()) // Newest first
      .reverse(); // Then reverse so newest is at the right
    
    return sortedEntries.map(([dateKey, count]) => ({
      date: new Date(dateKey).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      }),
      count,
      fullDate: dateKey // Keep for debugging
    }));
  })();

  if (processedData.length === 0) {
    return (
      <div className="mt-4 p-4 bg-card border border-border rounded-lg">
        <h4 className="text-sm font-medium text-card-foreground mb-3">{title}</h4>
        <div className="h-48 flex items-center justify-center">
          <p className="text-sm text-muted-foreground">No data available for the selected period</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 p-4 bg-card border border-border rounded-lg">
      <h4 className="text-sm font-medium text-card-foreground mb-3">{title}</h4>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={processedData}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="date" 
              fontSize={12}
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              tickLine={{ stroke: 'hsl(var(--border))' }}
            />
            <YAxis 
              fontSize={12}
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              tickLine={{ stroke: 'hsl(var(--border))' }}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px',
                color: 'hsl(var(--card-foreground))'
              }}
            />
            <Line 
              type="monotone" 
              dataKey="count" 
              stroke={color}
              strokeWidth={2}
              dot={{ fill: color, strokeWidth: 0, r: 3 }}
              activeDot={{ r: 5, fill: color }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CoreValueChart;
